import type { Highlight } from '@morph-mapper/file-viewer';
import { SchemaVariant } from '@morph-mapper/types';

interface WordPosition {
  word: string;
  head: number;
  tail: number;
}

export interface Position {
  anchor?: {
    txt: string;
    head?: number;
    column?: number;
    row?: number;
  };
  selection?: {
    txt: string;
    head?: number;
    column?: number;
    row?: number;
  };
}

const splitTextIntoWordsWithPositions = (
  txt: string,
  head: number,
): WordPosition[] => {
  if (!txt) {
    return [];
  }

  const words: WordPosition[] = [];
  const wordRegex = /\S+/g;
  let match: RegExpExecArray | null;

  while ((match = wordRegex.exec(txt)) !== null) {
    const word = match[0];
    const wordHead = match.index + head;

    words.push({
      word,
      head: wordHead,
      tail: wordHead + word.length,
    });
  }

  return words;
};

const appendTableHighlights = (
  position: Position,
  highlights: Highlight[],
): void => {
  const column = position.selection?.column;
  const row = position.selection?.row;

  if (typeof column !== 'number' || typeof row !== 'number') {
    return;
  }

  highlights.push({
    head: row,
    tail: column,
    color: '#FFE8B8',
    txt: position.selection?.txt || '',
  });
};

const appendTextHighlights = (
  position: Position,
  highlights: Highlight[],
): void => {
  if (position.anchor?.head !== undefined) {
    for (const word of splitTextIntoWordsWithPositions(
      position.anchor.txt,
      position.anchor.head,
    )) {
      highlights.push({
        head: word.head,
        tail: word.tail,
        color: '#cfe8ff',
        txt: word.word,
      });
    }
  }

  if (position.selection?.head !== undefined) {
    for (const word of splitTextIntoWordsWithPositions(
      position.selection.txt,
      position.selection.head,
    )) {
      highlights.push({
        head: word.head,
        tail: word.tail,
        color: '#FFE8B8',
        txt: word.word,
      });
    }
  }
};

export const createHighlightsForPosition = (
  position: Position,
  variant: SchemaVariant,
): Highlight[] => {
  const highlights: Highlight[] = [];

  if (variant === SchemaVariant.Table) {
    appendTableHighlights(position, highlights);
  } else {
    appendTextHighlights(position, highlights);
  }

  return highlights;
};
